import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TicketPercent, X } from "lucide-react";
import { formatPrice, type CouponPublic, type Currency } from "@nh/shared";
import { Button, Input } from "@/shared/ui/core";
import { validateCoupon } from "@/features/admin/coupons/coupons.api";

export interface CouponFieldProps {
  currency: Currency;
  applied: CouponPublic | null;
  onApply: (coupon: CouponPublic) => void;
  onRemove: () => void;
}

/**
 * "¿Tienes un cupón?" — al FINAL del checkout (Anahí, 27-ago), junto al
 * botón de pagar. El campo valida el código contra el servidor y pinta
 * el estimado; el descuento REAL lo recalcula el servidor al crear la
 * orden con la misma aritmética compartida.
 *
 * Un código rechazado dice su porqué aquí mismo ("ya venció", "ya se
 * agotó") sin tirar el pedido: quien no tiene cupón sigue de largo.
 */
export function CouponField({ currency, applied, onApply, onRemove }: CouponFieldProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function aplicar() {
    if (!code.trim()) return;
    setError(null);
    setChecking(true);
    try {
      const coupon = await validateCoupon(code.trim().toUpperCase(), currency);
      onApply(coupon);
      setCode("");
    } catch (cause) {
      setError(
        cause instanceof Error && cause.message
          ? cause.message
          : t("No se pudo validar el cupón"),
      );
    } finally {
      setChecking(false);
    }
  }

  if (applied) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-ds-violet-pop/40 bg-ds-mist/50 px-4 py-3">
        <p className="flex items-center gap-2 text-[14px] text-ds-ink">
          <TicketPercent className="h-4 w-4 shrink-0 text-ds-violet-pop" />
          <span>
            {t("Cupón")} <span className="font-mono font-semibold">{applied.code}</span>{" "}
            <span className="text-ds-ink-3">
              (
              {applied.kind === "percent"
                ? t("{{n}}% de descuento", { n: applied.percent })
                : t("{{monto}} de descuento", {
                    monto: formatPrice(applied.amountCents ?? 0, currency),
                  })}
              )
            </span>
          </span>
        </p>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          leftIcon={<X />}
          aria-label={t("Quitar el cupón")}
          onClick={onRemove}
        />
      </div>
    );
  }

  return (
    <div className="grid gap-1.5">
      <p className="text-[13px] font-medium text-ds-ink-3">{t("¿Tienes un cupón?")}</p>
      <div className="flex items-center gap-2">
        <div className="w-48">
          <Input
            value={code}
            onChange={(event) => {
              setCode(event.target.value.toUpperCase());
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void aplicar();
              }
            }}
            placeholder="BIENVENIDA10"
            maxLength={24}
            aria-label={t("Código del cupón")}
          />
        </div>
        <Button
          variant="secondary"
          loading={checking}
          onClick={() => void aplicar()}
          type="button"
        >
          {t("Aplicar")}
        </Button>
      </div>
      {error && <p className="text-[12px] text-ds-terracotta">{error}</p>}
    </div>
  );
}
